<template>
  <div class="customer-devices">
    <!-- Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.type_to_search')"
          :disabled="showFleet"
        />
      </div>

      <div class="device-metrics">
        <div class="metric-badge">
          <span class="metric-label">{{ $t("globals.devices") }}</span>
          <span class="metric-value">{{ totalLoaded ? total : "-" }}</span>
        </div>
        <button
          v-if="web_app_id"
          type="button"
          class="metric-badge fleet-toggle"
          :class="[`fleet-${fleetBadgeVariant}`, { active: showFleet }]"
          v-b-tooltip.hover
          :title="showFleet ? $t('fleet.show_billing_devices') : $t('fleet.show_fleet_devices')"
          @click="toggleFleet"
        >
          <span class="metric-label">
            <v-icon v-if="showFleet" name="check" scale="0.7"></v-icon>
            Fleet
          </span>
          <span class="metric-value">{{ fleetDevicesCount != null ? fleetDevicesCount : "?" }}</span>
        </button>
      </div>

      <div class="action-buttons">
        <button
          v-if="devices != null && devices.length > 0"
          class="reload-btn"
          @click="downloadDevicesCSV"
          :disabled="downloadCsvLoading"
          v-b-tooltip.hover
          :title="$t('globals.download_csv')"
        >
          <v-icon name="file-download" :spin="downloadCsvLoading"></v-icon>
        </button>
      </div>
    </div>

    <!-- Fleet Table -->
    <div v-if="showFleet" class="table-wrapper" style="position: relative; min-height: 300px;">
      <div v-if="fleetLoading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <table class="modern-table">
        <thead>
          <tr>
            <th v-for="field in fleetFields" :key="field.key">{{ field.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!fleetLoading && fleetRows.length === 0">
            <td :colspan="fleetFields.length" class="empty-state-cell">
              {{ fleetDevicesFailed ? $t("fleet.fleet_lookup_failed") : $t("globals.no_results") }}
            </td>
          </tr>
          <tr
            v-for="item in fleetRows"
            :key="item.id"
            class="table-row"
            :class="{ 'row-missing': item.billing.state == 'missing' }"
          >
            <td>{{ item.id }}</td>
            <td>{{ item.imei }}</td>
            <td>
              <span class="status-badge" :class="`status-${item.status}`">{{ item.status }}</span>
            </td>
            <td>
              <span v-if="item.owner" :class="{ 'text-danger': !isOwnCompany(item.owner) }">
                {{ item.owner.name }}
              </span>
            </td>
            <td>
              <span v-if="item.payingEntity" :class="{ 'text-danger': !isOwnCompany(item.payingEntity) }">
                {{ item.payingEntity.name }}
              </span>
            </td>
            <td>
              <template v-if="item.vehicle">
                {{ item.vehicle.licensePlate || item.vehicle.name }}
                <span v-if="item.vehicle.state == 'unpaired'" class="status-badge pending">
                  {{ $t("fleet.unpaired") }}
                </span>
              </template>
            </td>
            <td>
              <span v-if="item.billing.state == 'failed'" class="status-badge neutral">
                {{ $t("fleet.billing_lookup_failed") }}
              </span>
              <span v-else-if="item.billing.state == 'missing'" class="status-badge fail">
                {{ $t("fleet.not_in_billing") }}
              </span>
              <router-link
                v-else-if="item.billing.state == 'own'"
                class="plain-pointer-link"
                :to="{ name: 'device', params: { id: item.billing.device.id } }"
                target="_blank"
              >
                {{ item.billing.device.id }}
              </router-link>
              <template v-else-if="item.billing.state == 'other'">
                <span class="status-badge pending">{{ $t("fleet.different_customer") }}</span>
                <router-link
                  v-if="item.billing.device.customer"
                  class="plain-pointer-link ml-1"
                  :to="{ name: 'customer', params: { id: item.billing.device.customer.id } }"
                  target="_blank"
                >
                  {{ item.billing.device.customer.company_name }}
                </router-link>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Billing Table -->
    <div v-else class="table-wrapper" style="position: relative; min-height: 300px;">
      <div v-if="devicesLoading || totalLoading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in billingFields"
              :key="field.key"
              :class="{ sortable: field.sortable, [field.class]: field.class }"
              @click="field.sortable && handleSort(field.key)"
            >
              <span
                v-if="field.key == 'fleet' && !web_app_id"
                v-b-tooltip.hover
                :title="$t('fleet.no_web_app_id')"
              >
                {{ field.label }}
              </span>
              <template v-else>{{ field.label }}</template>
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!devicesLoading && !totalLoading && filteredDevices.length === 0">
            <td :colspan="billingFields.length" class="empty-state-cell">
              {{ $t("globals.no_results") }}
            </td>
          </tr>
          <tr v-for="item in filteredDevices" :key="item.id" class="table-row">
            <td>{{ item.id }}</td>
            <td>
              <router-link
                class="plain-pointer-link"
                :to="{ name: 'device', params: { id: item.id } }"
                target="_blank"
              >
                {{ item.imei }}
              </router-link>
            </td>
            <td>{{ item.product }}</td>
            <td>{{ item.batch }}</td>
            <td>
              <span class="status-badge" :class="`status-${item.status}`">{{ item.status }}</span>
            </td>
            <td :class="{ 'col-disabled': !web_app_id }">
              <span v-if="!web_app_id" class="text-muted">-</span>
              <span
                v-else-if="fleetUnavailable"
                class="status-badge neutral fleet-retry"
                v-b-tooltip.hover
                :title="$t('fleet.retry')"
                @click="retryFleetDevicesStatus"
              >
                {{ $t("fleet.fleet_lookup_failed") }}
                <v-icon name="sync-alt" scale="0.7"></v-icon>
              </span>
              <template v-else-if="fleetDevices != null">
                <span
                  v-for="(badge, index) in fleetBadges(item.imei)"
                  :key="index"
                  class="status-badge mr-1"
                  :class="badgeClass(badge.variant)"
                >
                  {{ badge.text }}
                </span>
              </template>
            </td>
            <td>
              <span v-if="item.pending_termination != null" class="status-badge pending">
                {{ new Date(item.pending_termination.terminate_at).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric'}) }}
              </span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">
          {{ $t("globals.showing") }} {{ pageStart }}-{{ pageEnd }} {{ $t("globals.of") }} {{ pageTotal }}
        </span>
        <select v-model="perPage" class="per-page-select">
          <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
        </select>
      </div>
      <div class="pagination-controls">
        <button class="page-btn" :disabled="activePage <= 1 || pageLoading" @click="goToPage(activePage - 1)">
          &lt;
        </button>
        <button class="page-btn" :disabled="activePage >= pageCount || pageLoading" @click="goToPage(activePage + 1)">
          &gt;
        </button>
      </div>
    </div>

    <!-- Hidden download element -->
    <a
      href=""
      id="csvdownloadlink"
      ref="csvfiledownload"
      hidden="true"
      download=""
    ></a>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  props: {
    customer:{
      type: Number,
      required: true
    },
    web_app_id: Number,
  },
  data() {
    return {
      total : 0,
      fleetDevicesCount: null,
      fleetDevices: null,
      showFleet: false,
      fleetLoading: false,
      fleetItems: [],
      fleetTotal: 0,
      fleetPage: 1,
      fleetRequestId: 0,
      fleetFields: [
        { key: "id", label: "ID" },
        { key: "imei", label: "IMEI" },
        { key: "status", label: this.$t("globals.status") },
        { key: "owner", label: this.$t("fleet.owner") },
        { key: "payingEntity", label: this.$t("fleet.paying_entity") },
        { key: "vehicle", label: this.$t("fleet.vehicle") },
        { key: "billing", label: this.$t("fleet.billing") }
      ],
      billingDevices: null,
      billingLookupFailed: false,
      // Set after a failed Fleet status lookup so paging does not raise one error toast per page
      fleetUnavailable: false,
      fleetDevicesFailed: false,
      billingStale: false,
      totalLoaded: false,
      devices: [],
      devicesLoading: false,
      totalLoading: false,
      devicesRequestId: 0,
      downloadCsvLoading: false,
      filter: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      sortBy :"id",
      sortDesc:false,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true
        },
        {
          key: "imei",
          label: "IMEI",
          sortable: true
        },
        {
          key: "product",
          label: this.$t("globals.product"),
          sortable: true
        },
        {
          key: "batch",
          label: this.$t("globals.batch"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "fleet",
          label: "Fleet"
        },
        {
          key: "terminate_at",
          label: this.$t("globals.terminate_scheduled_by_customer"),
          sortable: true
        }
      ]
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch", "simpleWebAppFetch"]),

    fetchFleetDevicesCount() {
      const webAppId = this.web_app_id;
      if (!webAppId) return;
      const data = {
        variables: { id: webAppId },
        query: `
          query CompanyDevicesCount($id: ID!) {
            company(id: $id) {
              devicesCount
            }
          }
        `
      };
      this.simpleWebAppFetch({ data, this_ref: this }).then(res => {
        // Ignore responses for a company that is no longer linked
        if (res && res.company && webAppId == this.web_app_id) {
          this.fleetDevicesCount = res.company.devicesCount;
        }
      });
    },

    fetchFleetDevicesStatus() {
      const devices = this.devices;
      const webAppId = this.web_app_id;
      // The previous map is kept until the new one arrives so re-sorting does not blank the column
      if (!webAppId || this.fleetUnavailable || !Array.isArray(devices)) return;
      const imeis = devices.filter(d => d.imei).map(d => String(d.imei));
      if (imeis.length == 0) return;

      const data = {
        variables: { imeis },
        query: `
          query DevicesByImeis($imeis: [String!]!) {
            DevicesByImeis(imeis: $imeis) {
              imei
              owner { id }
              payingEntity { id }
              vehicle {
                licensePlate
                name
                state
              }
            }
          }
        `
      };
      this.simpleWebAppFetch({ data, this_ref: this }).then(res => {
        // Ignore responses for a page or company that is no longer displayed
        if (this.devices !== devices || webAppId != this.web_app_id) return;
        if (!res) {
          this.fleetUnavailable = true;
          return;
        }
        // Every requested IMEI gets an entry, null when Fleet does not know it
        const found = res.DevicesByImeis || [];
        this.fleetDevices = imeis.reduce((acc, imei) => {
          acc[imei] = found.find(d => d.imei == imei) || null;
          return acc;
        }, {});
      });
    },

    fleetBadges(imei) {
      // Not looked up yet (new page still loading)
      if (!(imei in this.fleetDevices)) return [];
      const fleetDevice = this.fleetDevices[imei];
      if (!fleetDevice) {
        return [{ variant: "danger", text: this.$t("fleet.not_in_fleet") }];
      }

      const badges = [];
      const vehicle = fleetDevice.vehicle;
      if (vehicle && vehicle.state == "unpaired") {
        badges.push({ variant: "warning", text: this.$t("fleet.unpaired") });
      } else if (vehicle && (vehicle.licensePlate || vehicle.name)) {
        badges.push({ variant: "info", text: vehicle.licensePlate || vehicle.name });
      }

      // A missing owner or paying entity is informational only, not a mismatch
      const isOtherCompany = entity => entity != null && !this.isOwnCompany(entity);
      if (isOtherCompany(fleetDevice.owner) || isOtherCompany(fleetDevice.payingEntity)) {
        badges.push({ variant: "danger", text: this.$t("fleet.different_owner_or_paying_entity") });
      }
      return badges;
    },

    isOwnCompany(entity) {
      return entity != null && String(entity.id) == String(this.web_app_id);
    },

    retryFleetDevicesStatus() {
      this.fleetUnavailable = false;
      this.fetchFleetDevicesStatus();
    },

    toggleFleet() {
      this.showFleet = !this.showFleet;
      // Search is disabled in the Fleet view, so drop any filter instead of keeping it hidden
      this.filter = null;
      if (this.showFleet) {
        this.fetchFleetDevices();
        return;
      }

      // The Fleet table loaded fine, so a previous status failure is worth retrying once
      const retryStatus = this.fleetUnavailable && !this.fleetDevicesFailed;
      if (retryStatus) {
        this.fleetUnavailable = false;
      }
      if (this.billingStale) {
        // fetchDevices already refreshes the Fleet status of the new page
        this.billingStale = false;
        this.fetchDevices();
      } else if (retryStatus) {
        this.fetchFleetDevicesStatus();
      }
    },

    // Looks up the Fleet page IMEIs in Billing to find the ones missing or under another customer
    fetchBillingDevices(fleetItems, requestId) {
      this.billingDevices = null;
      this.billingLookupFailed = false;
      const imeis = fleetItems.filter(d => d.imei).map(d => String(d.imei));
      if (imeis.length == 0) {
        this.billingDevices = {};
        return;
      }

      const data = {
        variables: { imeis },
        query: `
          query devicesByImeis($imeis: [String]!) {
            devicesByImeis(imeis: $imeis) {
              id
              imei
              customer {
                id
                company_name
              }
            }
          }
        `
      };
      this.simpleFetch({ data, this_ref: this }).then(res => {
        if (requestId != this.fleetRequestId) return;
        if (!res) {
          this.billingLookupFailed = true;
          return;
        }
        const devices = res.devicesByImeis || [];
        this.billingDevices = imeis.reduce((acc, imei) => {
          // Prefer the device of this customer if the IMEI exists more than once
          const matches = devices.filter(d => d.imei == imei);
          acc[imei] = matches.find(d => this.isBillingCustomer(d)) || matches[0] || null;
          return acc;
        }, {});
      });
    },

    isBillingCustomer(billingDevice) {
      return billingDevice.customer != null && String(billingDevice.customer.id) == String(this.customer);
    },

    // State of a Fleet row in Billing: pending, failed, missing, own (this customer) or other
    billingMatch(imei) {
      if (this.billingLookupFailed) return { state: "failed" };
      if (this.billingDevices == null) return { state: "pending" };
      const device = this.billingDevices[imei];
      if (!device) return { state: "missing" };
      return { state: this.isBillingCustomer(device) ? "own" : "other", device };
    },

    // Maps the bootstrap variants returned by fleetBadges to the global status-badge colours
    badgeClass(variant) {
      return { danger: "fail", warning: "pending", info: "ok" }[variant] || "neutral";
    },

    handleSort(key) {
      if (this.sortBy == key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = key;
        this.sortDesc = false;
      }
    },

    goToPage(page) {
      if (this.showFleet) {
        this.fleetPage = page;
      } else {
        this.currentPage = page;
      }
    },

    // Reloads the Fleet table from the first page; changing fleetPage already triggers its watcher
    reloadFleetDevices() {
      if (!this.showFleet) {
        // The fleetPage watcher does not fetch while hidden; the next toggle starts from page 1
        this.fleetPage = 1;
        return;
      }
      if (this.fleetPage != 1) {
        this.fleetPage = 1;
      } else {
        this.fetchFleetDevices();
      }
    },

    resetFleet() {
      this.fleetRequestId++;
      this.fleetDevicesCount = null;
      this.fleetDevices = null;
      this.fleetItems = [];
      this.fleetTotal = 0;
      this.fleetLoading = false;
      this.fleetUnavailable = false;
      this.fleetDevicesFailed = false;
      this.billingDevices = null;
      this.billingLookupFailed = false;
      if (!this.web_app_id) {
        this.showFleet = false;
        if (this.billingStale) {
          this.billingStale = false;
          this.fetchDevices();
        }
      }
    },

    fetchFleetDevices() {
      if (!this.web_app_id) return;
      const requestId = ++this.fleetRequestId;
      const data = {
        variables: {
          id: this.web_app_id,
          page: this.fleetPage,
          perPage: this.perPage
        },
        query: `
          query CompanyDevices($id: ID!, $page: Int, $perPage: Int) {
            company(id: $id) {
              devices(page: $page, perPage: $perPage) {
                totalCount
                items {
                  id
                  imei
                  status
                  owner { id name }
                  payingEntity { id name }
                  vehicle { licensePlate name state }
                }
              }
            }
          }
        `
      };

      this.fleetLoading = true;
      this.simpleWebAppFetch({ data, this_ref: this })
        .then(res => {
          // Ignore responses for a page that is no longer displayed
          if (requestId != this.fleetRequestId) return;
          const devices = res && res.company && res.company.devices;
          this.fleetDevicesFailed = !devices;
          this.fleetItems = devices ? devices.items : [];
          if (devices) {
            this.fleetTotal = devices.totalCount;
            this.fetchBillingDevices(this.fleetItems, requestId);
          }
          // On failure fleetTotal keeps its last value, otherwise b-pagination would
          // reset fleetPage to 1 and its watcher would send another request
        })
        .finally(() => {
          if (requestId == this.fleetRequestId) {
            this.fleetLoading = false;
          }
        });
    },

    fetchTotalDevices(){
      const data = {
        query: `query { Customer(id:${parseInt(this.customer)}){ devices_total }}`
      };
      // Separate from devicesLoading so the total watcher's fetchDevices does not make the overlay flicker
      this.totalLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.total = res.Customer.devices_total;
            this.totalLoaded = true;
          }
        })
        .finally(() => {
          this.totalLoading = false;
      });
    },
    fetchDevices() {
      const offset = (this.currentPage-1) * this.perPage;
      const sort = this.sortDesc ? 'desc' : 'asc'
      const data = {
        query: `
        query { 
          Customer(id:${parseInt(this.customer)}){
            devicesPagination (
            sortBy:"${this.sortBy}", 
            sort:"${sort}"
            limit: ${parseInt(this.perPage)},
            offset: ${parseInt(offset)}
          ) { 
              id
              imei
              status
              product
              batch
              pending_termination{
                terminate_at
              }
            }
          }
          
        }
        `
      };

      const requestId = ++this.devicesRequestId;
      this.devicesLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          // Ignore responses for a sort or page that is no longer requested
          if (res && requestId == this.devicesRequestId) {
            this.devices = (res.Customer && res.Customer.devicesPagination) || [];
            this.fetchFleetDevicesStatus();
          }
        })
        .finally(() => {
          if (requestId == this.devicesRequestId) {
            this.devicesLoading = false;
          }
        });
    },
   
    downloadDevicesCSV() {
      this.downloadCsvLoading = true;
      let data = {
        query: `query {
          DevicesForCustomerCsv(
            customer_id: ${parseInt(this.$route.params.id)}
          )
        }`
      };

      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.startDownloadCSV(res.DevicesForCustomerCsv);
        }
      }).finally(()=>{
        this.downloadCsvLoading = false;
      });
    },
    startDownloadCSV(csv) {
      let d = new Date();
      let d_string = `${d.getUTCFullYear()}-`;
      d_string += `${("0" + (d.getUTCMonth() + 1)).slice(-2)}-`;
      d_string += `${("0" + d.getUTCDate()).slice(-2)}`;

      let link = document.getElementById("csvdownloadlink");
      link.href = "data:Application/octet-stream," + encodeURIComponent(csv);
      link.style.display = "block";
      link.download = `Customer_id_${this.$route.params.id}_devices_${d_string}.csv`;
      link.click();
    }
  },

  computed: {
    fleetRows() {
      return this.fleetItems.map(item => ({ ...item, billing: this.billingMatch(item.imei) }));
    },
    // Greys out the Fleet column for customers without a linked WebApp company
    billingFields() {
      if (this.web_app_id) return this.fields;
      return this.fields.map(field =>
        field.key == "fleet" ? { ...field, class: "col-disabled" } : field
      );
    },
    // Search only filters the billing page that is loaded
    filteredDevices() {
      if (!this.filter) return this.devices;
      const term = this.filter.toLowerCase();
      return this.devices.filter(device =>
        ["id", "imei", "product", "batch", "status"].some(key =>
          String(device[key] == null ? "" : device[key]).toLowerCase().includes(term)
        )
      );
    },
    activePage() {
      return this.showFleet ? this.fleetPage : this.currentPage;
    },
    pageTotal() {
      return this.showFleet ? this.fleetTotal : this.total;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.pageTotal / this.perPage));
    },
    pageStart() {
      return this.pageTotal == 0 ? 0 : (this.activePage - 1) * this.perPage + 1;
    },
    pageEnd() {
      return Math.min(this.activePage * this.perPage, this.pageTotal);
    },
    pageLoading() {
      return this.showFleet ? this.fleetLoading : this.devicesLoading;
    },
    sortState() {
      return `${this.sortBy}|${this.sortDesc}`;
    },
    fleetBadgeVariant() {
      // Stay neutral until the billing total is known so the badge does not flash red
      if (!this.totalLoaded || this.fleetDevicesCount == null) return "secondary";
      return this.fleetDevicesCount != this.total ? "danger" : "success";
    }
  },

  mounted() {
    this.fetchTotalDevices();
    this.fetchFleetDevicesCount();
  },
  watch:{
    web_app_id() {
      this.resetFleet();
      this.fetchFleetDevicesCount();
      this.fetchFleetDevicesStatus();
      this.reloadFleetDevices();
    },
    fleetPage() {
      if (this.showFleet) {
        this.fetchFleetDevices();
      }
    },
    total(n,o){
      if(this.total > 0){
        this.fetchDevices();
      }
    },
    currentPage(n,o){
      if (this.showFleet) {
        this.billingStale = true;
        return;
      }
      this.fetchDevices();
    },
    perPage(n,o){
      // The billing table is hidden in the Fleet view; reload it when the user switches back
      if (this.showFleet) {
        this.billingStale = true;
        // Fewer pages may exist with the new size; the next billing load starts from page 1
        this.currentPage = 1;
      } else if (this.currentPage != 1) {
        // The currentPage watcher fetches the first page
        this.currentPage = 1;
      } else {
        this.fetchDevices();
      }
      this.reloadFleetDevices();
    },
    // One watcher for both so switching column and direction together fetches once
    sortState(n,o){
      this.fetchDevices();
      // this.currentPage = 1; // To stay in the current page keep this commented, if after a sort it needs to go to the first page, uncomment
    }
  }
  
};
</script>

<style scoped>
/* Rows are not clickable, only the links inside them */
.modern-table tbody tr.table-row:hover {
  cursor: default;
}

.device-metrics {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.fleet-toggle {
  cursor: pointer;
  transition: all 0.2s;
}

.fleet-toggle:hover,
.fleet-toggle.active {
  border-color: #ff6b35;
}

.fleet-toggle.fleet-success .metric-value {
  color: #388e3c;
}

.fleet-toggle.fleet-danger .metric-value {
  color: #d32f2f;
}

.fleet-toggle.fleet-secondary .metric-value {
  color: #9ca3af;
}

.fleet-retry {
  cursor: pointer;
}

.plain-pointer-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.plain-pointer-link:hover,
.plain-pointer-link:focus {
  color: #0d6efd;
  text-decoration: underline;
}

.status-badge.neutral {
  background-color: #f5f5f5;
  color: #757575;
}

.col-disabled {
  background-color: #f5f5f5;
  color: #9ca3af;
}

.row-missing td {
  background-color: #ffebee;
}
</style>
